import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  ReactNode,
} from "react";
import {
  CaseManagementEvent,
  CreateCaseManagementEventRequest,
  UpdateCaseManagementEventRequest,
  CaseManagementEventsFilter,
  CaseManagementAggregates,
} from "@visitproof/types";
import { CaseManagementService } from "../services/CaseManagementService";

interface Errors {
  list?: Error | null;
  create?: Error | null;
  update?: Error | null;
  delete?: Error | null;
}

interface Loadings {
  list?: boolean;
  create?: boolean;
  update?: boolean;
  delete?: boolean;
}

interface CaseManagementContextProps {
  events: CaseManagementEvent[];
  loading: Loadings;
  error: Errors;
  total: number;
  hasMore: boolean;
  fetchEvents: (
    caseId: string,
    filter?: Omit<CaseManagementEventsFilter, "caseId">,
  ) => Promise<void>;
  loadMore: (caseId: string) => Promise<void>;
  createEvent: (
    data: CreateCaseManagementEventRequest,
  ) => Promise<CaseManagementEvent>;
  updateEvent: (
    eventId: string,
    data: Omit<UpdateCaseManagementEventRequest, "eventId">,
  ) => Promise<CaseManagementEvent>;
  deleteEvent: (eventId: string) => Promise<void>;
  clearEvents: () => void;
}

const CaseManagementContext = createContext<
  CaseManagementContextProps | undefined
>(undefined);

export const useCaseManagement = (): CaseManagementContextProps => {
  const context = useContext(CaseManagementContext);
  if (!context) {
    throw new Error(
      "useCaseManagement must be used within a CaseManagementProvider",
    );
  }
  return context;
};

const PAGE_SIZE = 20;

export const CaseManagementProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [events, setEvents] = useState<CaseManagementEvent[]>([]);
  const [total, setTotal] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [currentFilter, setCurrentFilter] = useState<
    Omit<CaseManagementEventsFilter, "caseId"> | undefined
  >();
  const [loading, setLoading] = useState<Loadings>({});
  const [error, setError] = useState<Errors>({});

  const fetchEvents = useCallback(
    async (
      caseId: string,
      filter?: Omit<CaseManagementEventsFilter, "caseId">,
    ) => {
      setLoading((prev) => ({ ...prev, list: true }));
      setError((prev) => ({ ...prev, list: null }));
      setCurrentFilter(filter);

      try {
        const response = await CaseManagementService.getEvents(caseId, filter, {
          limit: PAGE_SIZE,
          offset: 0,
        });
        setEvents(response.events);
        setTotal(response.totalCount);
        setHasMore(response.hasMore);
      } catch (err) {
        console.error("Error fetching case management events:", err);
        setError((prev) => ({ ...prev, list: err as Error }));
      } finally {
        setLoading((prev) => ({ ...prev, list: false }));
      }
    },
    [],
  );

  const loadMore = useCallback(
    async (caseId: string) => {
      if (!hasMore || loading.list) return;

      setLoading((prev) => ({ ...prev, list: true }));

      try {
        const response = await CaseManagementService.getEvents(
          caseId,
          currentFilter,
          {
            limit: PAGE_SIZE,
            offset: events.length,
          },
        );
        setEvents((prev) => [...prev, ...response.events]);
        setHasMore(response.hasMore);
      } catch (err) {
        console.error("Error loading more events:", err);
        setError((prev) => ({ ...prev, list: err as Error }));
      } finally {
        setLoading((prev) => ({ ...prev, list: false }));
      }
    },
    [events.length, hasMore, loading.list, currentFilter],
  );

  const createEvent = useCallback(
    async (
      data: CreateCaseManagementEventRequest,
    ): Promise<CaseManagementEvent> => {
      setLoading((prev) => ({ ...prev, create: true }));
      setError((prev) => ({ ...prev, create: null }));

      try {
        const newEvent = await CaseManagementService.createEvent(data);
        // Add new event at the beginning (most recent first)
        setEvents((prev) => [newEvent, ...prev]);
        setTotal((prev) => prev + 1);
        return newEvent;
      } catch (err) {
        console.error("Error creating case management event:", err);
        setError((prev) => ({ ...prev, create: err as Error }));
        throw err;
      } finally {
        setLoading((prev) => ({ ...prev, create: false }));
      }
    },
    [],
  );

  const updateEvent = useCallback(
    async (
      eventId: string,
      data: Omit<UpdateCaseManagementEventRequest, "eventId">,
    ): Promise<CaseManagementEvent> => {
      setLoading((prev) => ({ ...prev, update: true }));
      setError((prev) => ({ ...prev, update: null }));

      try {
        const updatedEvent = await CaseManagementService.updateEvent(
          eventId,
          data,
        );
        setEvents((prev) =>
          prev.map((event) => (event.id === eventId ? updatedEvent : event)),
        );
        return updatedEvent;
      } catch (err) {
        console.error("Error updating case management event:", err);
        setError((prev) => ({ ...prev, update: err as Error }));
        throw err;
      } finally {
        setLoading((prev) => ({ ...prev, update: false }));
      }
    },
    [],
  );

  const deleteEvent = useCallback(async (eventId: string): Promise<void> => {
    setLoading((prev) => ({ ...prev, delete: true }));
    setError((prev) => ({ ...prev, delete: null }));

    try {
      await CaseManagementService.deleteEvent(eventId);
      setEvents((prev) => prev.filter((event) => event.id !== eventId));
      setTotal((prev) => prev - 1);
    } catch (err) {
      console.error("Error deleting case management event:", err);
      setError((prev) => ({ ...prev, delete: err as Error }));
      throw err;
    } finally {
      setLoading((prev) => ({ ...prev, delete: false }));
    }
  }, []);

  const clearEvents = useCallback(() => {
    setEvents([]);
    setTotal(0);
    setHasMore(false);
    setCurrentFilter(undefined);
    setError({});
  }, []);

  return (
    <CaseManagementContext.Provider
      value={{
        events,
        loading,
        error,
        total,
        hasMore,
        fetchEvents,
        loadMore,
        createEvent,
        updateEvent,
        deleteEvent,
        clearEvents,
      }}
    >
      {children}
    </CaseManagementContext.Provider>
  );
};
